import { useEffect, useState } from "react";
import api from "@/lib/api";
import EmailNav from "./EmailNav";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Trash2, Pencil, Inbox } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", brand: "any" };

export default function ListsPage() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    setLoading(true);
    api.get("/email/lists")
      .then(({ data }) => setLists(data.lists || []))
      .catch(() => toast.error("Failed to load lists"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) await api.patch(`/email/lists/${editing.id}`, form);
      else await api.post("/email/lists", form);
      toast.success("Saved");
      setShowEditor(false); setEditing(null); setForm(EMPTY); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this list? Contacts on it will not be deleted.")) return;
    try { await api.delete(`/email/lists/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const open = (list) => {
    if (list) { setEditing(list); setForm({ name: list.name, description: list.description || "", brand: list.brand || "any" }); }
    else { setEditing(null); setForm(EMPTY); }
    setShowEditor(true);
  };

  return (
    <div data-testid="lists-page">
      <EmailNav />
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Lists</h2>
          <p className="text-xs text-muted-foreground">Organize contacts into named groups for targeted campaigns.</p>
        </div>
        <Button size="sm" onClick={() => open(null)} data-testid="lists-new-btn">
          <Plus size={14} className="mr-2" /> New List
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      ) : lists.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-sm text-muted-foreground">
          No lists yet. <button onClick={() => open(null)} className="text-primary hover:underline">Create your first list →</button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {lists.map((l) => (
            <Card key={l.id} data-testid={`list-card-${l.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Inbox size={16} className="text-primary" />
                    <h3 className="font-semibold text-sm">{l.name}</h3>
                  </div>
                  <Badge variant="outline" className="text-[10px] capitalize">{l.brand}</Badge>
                </div>
                {l.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{l.description}</p>}
                <div className="text-2xl font-bold mb-3">{l.count || 0}<span className="text-xs font-normal text-muted-foreground ml-1">contacts</span></div>
                <div className="flex gap-1">
                  <Button data-testid={`list-edit-${l.id}`} variant="ghost" size="sm" onClick={() => open(l)}><Pencil size={13} /></Button>
                  <Button data-testid={`list-delete-${l.id}`} variant="ghost" size="sm" onClick={() => remove(l.id)}>
                    <Trash2 size={13} className="text-rose-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit List" : "New List"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="list-form-name" placeholder="Name (e.g. RRDC Alumni Families)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={save} data-testid="list-form-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
